import EllipsisImage from "assets/images/ellipsis-design.png";
import { EProjectFormat } from "common/enums/ProjectFormat/ProjectFormat";
import { EProjectTheme } from "common/enums/ProjectTheme/ProjectTheme";
import ProjectCreateRequestResource from "common/resources/Project/ProjectCreateRequestResource";
import { ValidationError } from "common/resources/Resource";
import Button, { EButtonColor, EButtonSize, EButtonVariant } from "components/elements/Button";
import Typography from "components/elements/Typography";
import Form from "components/materials/Form";
import DragAndDropFile from "components/materials/Form/DragAndDropFileElement";
import Input from "components/materials/Form/InputElement";
import Select from "components/materials/Form/SelectElement";
import { TextArea } from "components/materials/Form/TextAreaElement";
import I18n from "components/materials/I18n";
import ThemeSelector from "components/materials/ThemeSelector";
import PageTemplate from "components/PageTemplate";
import ModuleConfig from "configs/ModuleConfig";
import { UserContext } from "contexts/User";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ProjectService from "services/ProjectService";
import { container } from "tsyringe";
import classes from "./classes.module.scss";
import assert from "assert";
import { EProjectPurpose } from "common/enums/ProjectPurpose";
import Checkbox from "components/elements/Checkbox";
import { ELang } from "common/enums/User/ELang";

const projectService = container.resolve(ProjectService);
const modules = container.resolve(ModuleConfig).get().modules;

export default function SubmitProject() {
	const formRef = useRef<HTMLFormElement | null>(null);
	const navigate = useNavigate();
	const [errors, setErrors] = useState<ValidationError[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isCvAdded, setIsCvAdded] = useState<boolean>(false);
	const [selectedTheme, setSelectedTheme] = useState<EProjectTheme | null>(null);
	const [selectedPurposes, setSelectedPurposes] = useState<EProjectPurpose[]>([]);
	const { user } = useContext(UserContext);

	useEffect(() => {
		if (!user?.address || !user?.city || !user?.zipCode || !user?.phoneNumber || !user?.gender || !user?.birthDate) {
			navigate(modules.pages.CompleteProfile.props.path);
		}
	}, [navigate, user]);

	const onThemeSelect = useCallback((theme: EProjectTheme) => {
		setSelectedTheme(theme);
	}, []);

	const onSubmit = useCallback(
		(formData: { [key: string]: unknown }) => {
			setErrors([]);
			setIsLoading(true);

			assert(selectedTheme, "No selected theme");
			assert(selectedPurposes.length > 0, "No selected purpose");

			const projectCreateRequest = ProjectCreateRequestResource.hydrate<ProjectCreateRequestResource>({
				title: formData["title"] as string,
				pitch: formData["pitch"] as string,
				format: formData["format"] as EProjectFormat,
				discord: formData["discord"] ? (formData["discord"] as string) : undefined,
				instagram: formData["instagram"] ? (formData["instagram"] as string) : undefined,
				x: formData["x"] ? (formData["x"] as string) : undefined,
				linkedin: formData["linkedin"] ? (formData["linkedin"] as string) : undefined,
				theme: selectedTheme,
				purposes: selectedPurposes,
				lang: I18n.getLang().toLocaleUpperCase() as ELang,
			});

			const scenario = (formData["scenario"] as FileList)?.[0];
			const cv = (formData["cv"] as FileList)?.[0];

			if (!cv && !formData["linkedin"]) {
				setErrors([{ property: "linkedin", constraints: { isNotEmpty: I18n.trslt(I18n.asset.pages.submit_project.labels.project_linkedin) } }]);
				setIsLoading(false);
				return;
			}

			projectCreateRequest
				.validateOrReject()
				.then(() => {
					projectService
						.createProject(projectCreateRequest, { scenario, cv })
						.then(() => {
							navigate(modules.pages.MyProjects.props.path);
						})
						.catch((error) => {
							setIsLoading(false);
							console.error({ error });
							if (error instanceof Array) {
								setErrors(error);
							}
						});
				})
				.catch((error) => {
					setIsLoading(false);
					console.error({ error });
					if (error instanceof Array) {
						setErrors(error);
					}
				});
		},
		[selectedTheme, navigate, setErrors, selectedPurposes],
	);

	const canSubmit = useCallback(() => {
		return !!selectedTheme && selectedPurposes.length > 0;
	}, [selectedTheme, selectedPurposes]);

	const addSelectedPurpose = useCallback(
		(purpose: EProjectPurpose) => {
			setSelectedPurposes((purposes: EProjectPurpose[]) => {
				if (!purposes.includes(purpose)) {
					return [...purposes, purpose];
				}
				return purposes;
			});
		},
		[setSelectedPurposes],
	);

	const removeSelectedPurpose = useCallback(
		(purpose: EProjectPurpose) => {
			setSelectedPurposes((purposes: EProjectPurpose[]) => {
				return purposes.filter((p) => p !== purpose);
			});
		},
		[setSelectedPurposes],
	);

	const handlePurposeChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>, purpose: EProjectPurpose) => {
			if (e.currentTarget.checked) {
				addSelectedPurpose(purpose);
			} else {
				removeSelectedPurpose(purpose);
			}
		},
		[addSelectedPurpose, removeSelectedPurpose],
	);

	return (
		<PageTemplate tabTitle={I18n.trslt(I18n.asset.pages.submit_project.page_title)} mainSectionClassName={classes["root"]}>
			<div className={classes["content"]}>
				<div className={classes["title-container"]}>
					<Typography type="h1" weight="bold">
						{I18n.trslt(I18n.asset.pages.submit_project.title)}
					</Typography>
					<Typography type="h3" weight="bold" className={classes["subtitle"]}>
						{I18n.trslt(I18n.asset.pages.submit_project.subtitle)}
					</Typography>
					<ul className={classes["description-items"]}>
						{I18n.asset.pages.submit_project.description.items.map(({ text }) => {
							const parts = text.split(/(<b>|<\/b>)/g).filter((part) => part !== "");

							return (
								<li key={text}>
									{parts.map((part, index) =>
										part === "<b>" || part === "</b>" ? null : (
											<Typography type="span" key={index} weight={parts[index - 1] === "<b>" ? "bold" : "regular"}>
												{part}
											</Typography>
										),
									)}
								</li>
							);
						})}
					</ul>
				</div>

				<Form onSubmit={onSubmit} errors={errors} className={classes["form-container"]} formRef={formRef}>
					<div className={classes["inputs-container"]}>
						<Input
							name="title"
							required
							className={classes["input"]}
							inputClassName={classes["input-content"]}
							label={I18n.trslt(I18n.asset.pages.submit_project.labels.project_title)}
							placeholder={I18n.trslt(I18n.asset.pages.submit_project.labels.project_title_placeholder)}
						/>
						<ThemeSelector onSelect={onThemeSelect} selectedTheme={selectedTheme} />

						<Select
							className={classes["select"]}
							name="format"
							label={I18n.trslt(I18n.asset.pages.submit_project.labels.project_format)}
							options={[
								{ label: I18n.trslt(I18n.asset.pages.submit_project.selections.format.selectAnOption), value: "empty" },
								{ label: I18n.trslt(I18n.asset.pages.submit_project.selections.format.long_metrage), value: EProjectFormat.long_metrage },
								{ label: I18n.trslt(I18n.asset.pages.submit_project.selections.format.court_metrage), value: EProjectFormat.court_metrage },
								{ label: I18n.trslt(I18n.asset.pages.submit_project.selections.format.serie), value: EProjectFormat.serie },
								{ label: I18n.trslt(I18n.asset.pages.submit_project.selections.format.documentaire), value: EProjectFormat.documentaire },
							]}
							required
						/>

						<Typography type="p" weight="regular" size="medium">
							{I18n.trslt(I18n.asset.pages.edit_project.labels.purpose)}
							{"*"}
						</Typography>

						<Checkbox
							checked={selectedPurposes.includes(EProjectPurpose.AUTHENTICITY_PROTECTION)}
							uid="AUTHENTICITY_PROTECTION"
							label={I18n.trslt(I18n.asset.pages.submit_project.selections.purposes.protect)}
							onChange={(e) => handlePurposeChange(e, EProjectPurpose.AUTHENTICITY_PROTECTION)}
						/>
						<Checkbox
							checked={selectedPurposes.includes(EProjectPurpose.FUNDING_APPLICATION)}
							uid="FUNDING_APPLICATION"
							label={I18n.trslt(I18n.asset.pages.submit_project.selections.purposes.funding)}
							sublabel={I18n.trslt(I18n.asset.pages.submit_project.selections.purposes.funding_comment)}
							onChange={(e) => handlePurposeChange(e, EProjectPurpose.FUNDING_APPLICATION)}
						/>
						<Checkbox
							checked={selectedPurposes.includes(EProjectPurpose.PITCH_TO_PROFESSIONALS)}
							uid="PITCH_TO_PROFESSIONALS"
							label={I18n.trslt(I18n.asset.pages.submit_project.selections.purposes.pitch)}
							onChange={(e) => handlePurposeChange(e, EProjectPurpose.PITCH_TO_PROFESSIONALS)}
						/>

						<TextArea
							name="pitch"
							className={classes["textarea"]}
							label={I18n.trslt(I18n.asset.pages.submit_project.labels.project_pitch)}
							placeholder={I18n.trslt(I18n.asset.pages.submit_project.labels.project_pitch_placeholder)}
							additionalInfo={I18n.trslt(I18n.asset.pages.submit_project.labels.project_pitch_additionnal)}
						/>

						<Input name="discord" className={classes["input"]} label={"Discord"} placeholder={"https://discord.gg/"} inputClassName={classes["input-content"]} />

						<Input name="instagram" className={classes["input"]} label={"Instagram"} placeholder={"https://instagram.com/"} inputClassName={classes["input-content"]} />

						<Input name="x" className={classes["input"]} label={"X"} placeholder={"https://x.com/"} inputClassName={classes["input-content"]} />
						<Input
							name="linkedin"
							className={classes["input"]}
							label={"Linkedin"}
							placeholder={"https://www.linkedin.com/in/"}
							required={!isCvAdded}
							inputClassName={classes["input-content"]}
							additionalInfo={I18n.trslt(I18n.asset.pages.submit_project.labels.project_linkedin)}
						/>

						<DragAndDropFile
							name="cv"
							accept={[".pdf"]}
							sub_description=".pdf"
							description={I18n.trslt(I18n.asset.pages.submit_project.drag_and_drop.cv)}
							label={I18n.trslt(I18n.asset.pages.submit_project.labels.cv)}
							onDrop={() => setIsCvAdded(true)}
							onFileChange={() => setIsCvAdded(true)}
							formRef={formRef}
						/>
						<DragAndDropFile
							name="scenario"
							accept={[".pdf"]}
							sub_description=".pdf"
							description={I18n.trslt(I18n.asset.pages.submit_project.drag_and_drop.scenario)}
							label={I18n.trslt(I18n.asset.pages.submit_project.labels.scenario)}
							keyErrorName={"sha256"}
							formRef={formRef}
						/>

						<Typography type="p" weight="regular" size="xsmall">
							{I18n.trslt(I18n.asset.pages.submit_project.confidentiality.scenarioProtection.text)}
						</Typography>
						<Typography type="p" weight="regular" size="xsmall">
							{I18n.trslt(I18n.asset.pages.submit_project.confidentiality.projectSharing.text)}{" "}
							<Link to={modules.pages.Subscribe.props.path}>{I18n.trslt(I18n.asset.pages.submit_project.confidentiality.projectSharing.suscribePageLink!)}</Link>
						</Typography>
					</div>

					<Button
						variant={EButtonVariant.CONTAINED}
						color={EButtonColor.PRIMARY}
						size={EButtonSize.LARGE}
						type="submit"
						isLoading={isLoading}
						fullwidth
						disabled={!canSubmit()}>
						<Typography type="span" size="medium" weight="medium">
							{I18n.trslt(I18n.asset.pages.submit_project.send_button)}
						</Typography>
					</Button>
				</Form>
			</div>
			<div className={classes["design"]}>
				<img src={EllipsisImage} alt="Ellipsis design" className={classes["image"]} />
			</div>
		</PageTemplate>
	);
}
